import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { SERVICE_CATEGORIES, COMPLIANCE_SERVICES, IT_SERVICES, SUPPLY_SERVICES, MANPOWER_SERVICES } from "../../data/servicesData";

const TABS = [
  { id: "supplies", label: "Supplies", services: SUPPLY_SERVICES },
  { id: "manpower", label: "Manpower", services: MANPOWER_SERVICES },
  { id: "compliance", label: "Compliance", services: COMPLIANCE_SERVICES },
  { id: "it", label: "IT Services", services: IT_SERVICES },
];

export function ServicesOverview() {
  const [activeTab, setActiveTab] = useState("supplies");
  const [userInteracted, setUserInteracted] = useState(false);
  const navigate = useNavigate();
  const activeData = TABS.find((t) => t.id === activeTab);
  const category = SERVICE_CATEGORIES.find((c) => c.id === activeTab);

  // Auto-toggle through tabs - STOPS when user clicks
  useEffect(() => {
    if (userInteracted) return; // Stop auto-toggle if user has clicked

    const interval = setInterval(() => {
      setActiveTab((currentTab) => {
        const currentIndex = TABS.findIndex((t) => t.id === currentTab);
        const nextIndex = (currentIndex + 1) % TABS.length;
        return TABS[nextIndex].id;
      });
    }, 4000);

    return () => clearInterval(interval);
  }, [userInteracted]);

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    setUserInteracted(true); // Lock the tab when user clicks
  };

  return (
    <section id="services" data-testid="services-section" className="py-14 md:py-18 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-red-600 font-bold text-xs tracking-[0.2em] uppercase font-body">
            What We Offer
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-[44px] font-black mt-3 mb-3 tracking-tight">
            Our Services
          </h2>
          <p className="text-slate-500 text-base md:text-lg font-body max-w-lg mx-auto">
            Comprehensive solutions for every business need — explore by category.
          </p>
        </motion.div>

        {/* Tab pills */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-white border border-slate-200 rounded-full p-1.5 shadow-sm gap-1 flex-wrap justify-center">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                data-testid={`services-tab-${tab.id}`}
                onClick={() => handleTabClick(tab.id)}
                className={`px-5 md:px-7 py-2.5 rounded-full font-extrabold text-sm transition-all duration-500 font-body ${
                  activeTab === tab.id
                    ? "bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg shadow-red-600/30"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Service cards with smooth transition */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
        >
          {activeData?.services.map((service, i) => (
            <ServiceCard key={service.slug || service.title} service={service} index={i} category={activeTab} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function ServiceCard({ service, index, category }) {
  const [hovered, setHovered] = useState(false);
  const navigate = useNavigate();
  const Icon = service.icon;

  const handleClick = () => {
    // Use the slug directly from service data
    const slug = service.slug || service.title.toLowerCase().replace(/\s+/g, "-");
    navigate(`/${slug}`);
    window.scrollTo(0, 0);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.08 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleClick}
      className="bg-white rounded-2xl p-7 border border-slate-100 transition-all duration-300 cursor-pointer flex flex-col gap-3"
      style={{
        boxShadow: hovered ? `0 24px 60px ${service.accentColor}20` : "0 2px 12px rgba(0,0,0,0.04)",
        transform: hovered ? "translateY(-4px)" : "none",
        borderColor: hovered ? `${service.accentColor}40` : "",
      }}
      data-testid={`service-card-${service.slug || service.title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-start justify-between">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ background: `${service.accentColor}12` }}
        >
          <Icon size={22} style={{ color: service.accentColor }} />
        </div>
        <div className="text-right">
          <div className="text-xl font-black font-body" style={{ color: service.accentColor }}>
            {service.stat}
          </div>
          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider font-body">
            {service.statLabel}
          </div>
        </div>
      </div>
      <h3 className="text-base font-extrabold text-slate-900 font-body">{service.title}</h3>
      <p className="text-[13px] text-slate-500 leading-relaxed font-body flex-1">{service.desc}</p>
      <div className="flex flex-wrap gap-1.5 mt-1">
        {service.features.slice(0, 3).map((f) => (
          <span
            key={f}
            className="text-[11px] font-semibold px-2.5 py-1 rounded-full font-body"
            style={{
              background: `${service.accentColor}08`,
              color: service.accentColor,
              border: `1px solid ${service.accentColor}20`,
            }}
          >
            {f}
          </span>
        ))}
      </div>
      {hovered && (
        <div className="flex items-center gap-1 text-sm font-bold mt-1" style={{ color: service.accentColor }}>
          Learn More <ArrowRight size={14} />
        </div>
      )}
    </motion.div>
  );
}
